import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { type RoleEnum } from "@/config/apiClient.types";
import { createApiClient } from "@/config/createApiClient";
import {
  selectedClientServiceIdAtom,
  selectedOrganisationIdAtom,
} from "@/store";
import { UsersApi } from "../../config/apiClient";

const apiClient = createApiClient(UsersApi);

export const useUser = () => {
  const selectedOrganisationIdAtomValue = useAtomValue(
    selectedOrganisationIdAtom
  );
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useGetUsers = (params: {
    searchText: string;
    role?: RoleEnum;
    enabled?: boolean;
  }) =>
    useQuery({
      queryKey: [
        "getUsers",
        params.searchText,
        params.role,
        selectedClientServiceIdAtomValue,
      ],
      queryFn: () =>
        apiClient.usersControllerGetUsersByClientService({
          clientServiceId: selectedClientServiceIdAtomValue,
          role: params.role,
          searchText: params.searchText,
        }),
      enabled:
        selectedClientServiceIdAtomValue > 0 &&
        (params.enabled !== undefined ? params.enabled : true),
    });

  const usePatchUser = () =>
    useMutation({
      mutationFn: (
        user: Parameters<
          typeof apiClient.usersControllerUpdateUser
        >["0"]["userUpdateCommandContainer"]["user"]
      ) =>
        apiClient.usersControllerUpdateUser({
          userUpdateCommandContainer: { user },
        }),
    });

  const usePatchUserAvatar = () =>
    useMutation({
      mutationFn: ({
        fileSignedId,
      }: Parameters<
        typeof apiClient.usersControllerUpdateUserAvatar
      >[0]["usersControllerUpdateUserAvatarRequest"]) =>
        apiClient.usersControllerUpdateUserAvatar({
          usersControllerUpdateUserAvatarRequest: { fileSignedId },
        }),
    });

  const useDeleteUserAvatar = () =>
    useMutation({
      mutationFn: () => apiClient.usersControllerDeleteUserAvatar(),
    });

  const useGetUserDefaultAddress = () =>
    useQuery({
      queryKey: ["getUserDefaultAddress", selectedOrganisationIdAtomValue],
      queryFn: () =>
        apiClient.usersControllerGetDefaultAddress({
          organisationId: selectedOrganisationIdAtomValue,
        }),
      enabled: Boolean(selectedOrganisationIdAtomValue),
    });

  const useSetDefaultAddress = () =>
    useMutation({
      mutationKey: ["setDefaultAddress"],
      mutationFn: ({ newDefaultAddressId }: { newDefaultAddressId: string }) =>
        apiClient.usersControllerSetDefaultAddress({
          organisationId: selectedOrganisationIdAtomValue,
          defaultAddressByUserSetCommand: { newDefaultAddressId },
        }),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["getUserDefaultAddress"],
          }),
        ]);
      },
    });

  return {
    useGetUsers,
    usePatchUser,
    usePatchUserAvatar,
    useDeleteUserAvatar,
    useGetUserDefaultAddress,
    useSetDefaultAddress,
  };
};
